"use client"

import { Col, Empty, Row, Spin } from "antd"
import TourItem from "../TourItem"
import { useDevicePassedWidth } from "@/hooks/app/useDevicePassedWidth"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { useToursByCategory } from "@/hooks/tour/useToursByCategory"
import { useToursCategories } from "@/hooks/tour/useToursCategories"
import PageLoading from "@/components/common/PageLoading"
import { useAppSelector } from "@/lib/redux/hooks"
import { useMemo } from "react"
import { TypeTourSearchResultItem } from "@/lib/redux/features/tour/types"
import { extractTourRouteLabel } from "../TopContent/TourCategoryRouteChips"
import { useTETranslation } from "@/hooks/app/useTETranslation"

const sortTours = (
  tours: TypeTourSearchResultItem[],
  sortKey: string | undefined,
  getValidDayjs: ReturnType<typeof useTETranslation>["getValidDayjs"]
) => {
  const list = [...tours]
  switch (sortKey) {
    case "1":
      return list.sort(
        (a, b) => parseFloat(a.priceDefault) - parseFloat(b.priceDefault)
      )
    case "2":
      return list.sort(
        (a, b) => (b.ratingsAverage || 0) - (a.ratingsAverage || 0)
      )
    case "3":
      return list.sort((a, b) => b.totalDay - a.totalDay)
    case "4":
      return list.sort(
        (a, b) =>
          getValidDayjs(a.startDate).valueOf() - getValidDayjs(b.startDate).valueOf()
      )
    case "5":
      return list.sort(
        (a, b) => parseFloat(b.priceDefault) - parseFloat(a.priceDefault)
      )
    case "6":
      return list.sort(
        (a, b) => parseFloat(a.priceDefault) - parseFloat(b.priceDefault)
      )
    default:
      return list
  }
}

const Tours = () => {
  const isIPad = useDevicePassedWidth((width) => width >= 768 && width <= 990)
  const isDesktop = useIsDesktop()
  const { data: tours, loading } = useToursByCategory()
  const { loading: loadingCategories } = useToursCategories()
  const priceRange = useAppSelector((state) => state.tour.formSearch.priceRange)
  const durationDays = useAppSelector((state) => state.tour.formSearch.durationDays)
  const routeFilter = useAppSelector((state) => state.tour.formSearch.routeFilter)
  const categorySort = useAppSelector((state) => state.tour.formSearch.categorySort)
  const { getValidDayjs } = useTETranslation()
  const filteredTours = useMemo(() => {
    let result = tours

    if (priceRange) {
      result = result.filter((tour) => {
        const price = parseFloat(tour.priceDefault)
        return price >= priceRange[0] && price <= priceRange[1]
      })
    }

    if (durationDays) {
      const [min, max] = durationDays
      result = result.filter(
        (tour) => tour.totalDay >= min && tour.totalDay <= max
      )
    }

    if (routeFilter) {
      result = result.filter((tour) => {
        const label = extractTourRouteLabel(tour.tourName)
        return label === routeFilter
      })
    }

    return sortTours(result, categorySort, getValidDayjs)
  }, [tours, priceRange, durationDays, routeFilter, categorySort, getValidDayjs])

  if (!!loadingCategories) {
    return <PageLoading />
  }

  return (
    <Spin
      spinning={!!loading || !!loadingCategories}
      indicator={
        <Row>
          <div className="loader" />
        </Row>
      }
    >
      <Row gutter={[0, 8]} className="tour-list__grid">
        {!filteredTours.length && !loading ? (
          <Col span={24}>
            <Empty />
          </Col>
        ) : (
          filteredTours.map((tour) => (
            <Col key={tour.id} span={24}>
              <TourItem tour={tour} type={isIPad || isDesktop ? "default" : "card"} />
            </Col>
          ))
        )}
      </Row>
    </Spin>
  )
}

export default Tours
